import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  Modal,
  FlatList,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import io from 'socket.io-client';
import { ChatService, UserService, AuthService } from '../../services/ApiService';
const SOCKET_URL = 'https://training-application-q6ln.onrender.com';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

const ChatScreen = () => {
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [chatType, setChatType] = useState('AI');
  const [isRestricted, setIsRestricted] = useState(false);
  const [socket, setSocket] = useState(null);
  const scrollViewRef = useRef(null);

  useEffect(() => {
    loadUserAndChat();
  }, []);

  useEffect(() => {
    if (user && user.approvedAt) {
      const approvedDate = new Date(user.approvedAt);
      const currentDate = new Date();
      const oneMonthInMs = 30 * 24 * 60 * 60 * 1000; // Approx 30 days

      if (currentDate - approvedDate > oneMonthInMs) {
        setIsRestricted(true);
      }
    }
  }, [user]);

  useEffect(() => {
    if (chatType) {
      loadChatHistory();
    }
  }, [chatType]);

  useEffect(() => {
    let newSocket;

    const setupSocket = async () => {
      // Only connect web sockets for Human chat since AI is instantaneous HTTP POST
      if (chatType !== 'Human') return;
      try {
        const token = await UserService.getFirebaseToken();
        newSocket = io(SOCKET_URL, {
          auth: { token },
        });

        newSocket.on('connect', () => {
          console.log('Socket connected successfully');
        });

        newSocket.on('new_message', (data) => {
          console.log('Socket received new_message:', data);
          if (data && data.success && data.message) {
            setMessages(prev => [...prev, data.message]);
            setTimeout(() => {
              scrollViewRef.current?.scrollToEnd({ animated: true });
            }, 100);
          }
        });

        setSocket(newSocket);
      } catch (error) {
        console.error('Socket setup error:', error);
      }
    };

    setupSocket();

    return () => {
      if (newSocket) newSocket.disconnect();
    };
  }, [chatType]);

  const loadUserAndChat = async () => {
    try {
      const [userData, sessions] = await Promise.all([
        UserService.getProfile(),
        ChatService.getChatSessions(),
      ]);

      setUser(userData);

      // Determine available chat type based on plan
      if (userData.planType === 'pro') {
        setChatType('AI');
      } else if (userData.planType === 'human') {
        setChatType('Human');
      }

      // Load chat history
      await loadChatHistory();
    } catch (error) {
      console.error('Load user and chat error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadChatHistory = async () => {
    try {
      const data = await ChatService.getChatHistory(chatType);
      // Messages should be in chronological order (oldest first, newest last)
      console.log('Chat history data:', data);
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Load chat history error:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    const messageText = newMessage.trim();
    setNewMessage('');
    setSending(true);

    // Add user message to UI immediately
    const userMessage = {
      sender: 'user',
      message: messageText,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      const response = await ChatService.sendMessage(messageText, chatType);

      // Add AI/trainer response to UI only if there's a reply
      if (response.reply && response.reply.trim()) {
        const botMessage = {
          sender: chatType === 'AI' ? 'ai' : 'trainer',
          message: response.reply,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, botMessage]);
      }

      // Scroll to bottom
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
    } catch (error) {
      console.error('Send message error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to send message. Please try again.';
      Alert.alert('Notice', errorMessage);

      // Remove the user message if sending failed
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  };


  const renderMessage = (message, index) => {
    const isUser = message.sender === 'user';
    const isAI = message.sender === 'ai';
    const isTrainer = message.sender === 'trainer';

    console.log(`Rendering message ${index}:`, message);

    return (
      <View
        key={index}
        style={[
          styles.messageContainer,
          isUser ? styles.userMessageContainer : styles.botMessageContainer,
        ]}
      >
        <View
          style={[
            styles.messageBubble,
            isUser ? styles.userMessageBubble : styles.botMessageBubble,
          ]}
        >
          <Text style={[
            styles.messageText,
            isUser ? styles.userMessageText : styles.botMessageText
          ]}>
            {message.message}
          </Text>
          <Text style={[
            styles.messageTime,
            isUser ? null : styles.botMessageTime
          ]}>
            {new Date(message.timestamp).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Text>
        </View>
        {!isUser && (
          <View style={styles.botAvatar}>
            <Ionicons
              name={isAI ? 'chatbubble' : 'person'}
              size={16}
              color="white"
            />
          </View>
        )}
      </View>
    );
  };

  const getChatTitle = () => {
    if (user?.planType === 'basic') {
      return 'Chat Not Available';
    }
    if (user?.planType === 'pro') {
      return 'AI Coach Chat';
    }
    if (user?.planType === 'human') {
      return 'Human Trainer Chat';
    }
    return 'Chat';
  };

  const getChatSubtitle = () => {
    if (user?.planType === 'basic') {
      return 'Upgrade to Pro for AI chat access';
    }
    if (user?.planType === 'pro') {
      return 'Ask your AI coach anything';
    }
    if (user?.planType === 'human') {
      return 'Chat with your personal trainer';
    }
    return '';
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea} edges={['top', 'left', 'right']}>
        <View style={styles.loadingContainer}>
          <Text style={{ color: '#FFFFFF' }}>Loading chat...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (isRestricted) {
    return (
      <SafeAreaView style={styles.safeArea} edges={['top', 'left', 'right']}>
        <View style={styles.container}>
          <View style={styles.header}>
            <Text style={styles.title}>Chat</Text>
            <Text style={styles.subtitle}>Check-in Assistance</Text>
          </View>
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
            <View style={{ backgroundColor: '#0B0E14', padding: 20, borderRadius: 50, marginBottom: 20 }}>
              <Ionicons name="lock-closed" size={48} color="#FF5252" />
            </View>
            <Text style={{ fontSize: 24, fontWeight: 'bold', color: '#FFFFFF', marginBottom: 12 }}>Access Expired</Text>
            <Text style={{ fontSize: 16, color: '#6D759E', textAlign: 'center', marginBottom: 10, lineHeight: 24 }}>
              Your access to this feature has expired because your plan is no longer active.
            </Text>
            <Text style={{ fontSize: 16, color: '#6D759E', textAlign: 'center', marginBottom: 30, fontStyle: 'italic' }}>
              Please renew your plan to continue using chat.
            </Text>
            <TouchableOpacity
              style={{ backgroundColor: '#3B82F6', width: 200, borderRadius: 12, height: 48, justifyContent: 'center', alignItems: 'center' }}
              onPress={() => navigation.navigate('Home')}
            >
              <Text style={{ color: 'white', fontWeight: 'bold', fontSize: 16 }}>Return to Home</Text>
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  if (user?.planType === 'basic') {
    return (
      <SafeAreaView style={styles.safeArea} edges={['top', 'left', 'right']}>
        <View style={styles.container}>
          <View style={styles.header}>
            <Text style={styles.title}>{getChatTitle()}</Text>
            <Text style={styles.subtitle}>{getChatSubtitle()}</Text>
          </View>

          <View style={styles.unavailableContainer}>
            <Ionicons name="chatbubble-outline" size={64} color="#ccc" />
            <Text style={styles.unavailableTitle}>Chat Not Available</Text>
            <Text style={styles.unavailableText}>
              Chat feature is only available for Pro and Human Trainer plans.
            </Text>
            <Text style={styles.unavailableSubtext}>
              Upgrade your plan to get access to AI coaching or human trainer support.
            </Text>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea} edges={['top', 'left', 'right']}>
      <SafeAreaView
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>{getChatTitle()}</Text>
          <Text style={styles.subtitle}>{getChatSubtitle()}</Text>
        </View>

        {/* Messages */}
        <ScrollView
          ref={scrollViewRef}
          style={styles.messagesContainer}
          contentContainerStyle={styles.messagesContent}
          onContentSizeChange={() => scrollViewRef.current?.scrollToEnd({ animated: true })}
        >
          {messages.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Ionicons name="chatbubble-outline" size={48} color="#6D759E" />
              <Text style={styles.emptyTitle}>Start a conversation</Text>
              <Text style={styles.emptyText}>
                {chatType === 'AI'
                  ? 'Ask your AI coach about workouts, nutrition, or any fitness questions!'
                  : 'Send a message to your trainer. They will respond as soon as possible.'
                }
              </Text>
              {/* <Text style={styles.emptyText}>
                Debug: Messages count: {messages.length}, ChatType: {chatType}
              </Text> */}
            </View>
          ) : (
            messages.map((message, index) => renderMessage(message, index))
          )}

          {sending && chatType === 'AI' && (
            <View style={styles.typingContainer}>
              <View style={styles.typingBubble}>
                <Text style={styles.typingText}>
                  AI is typing...
                </Text>
              </View>
            </View>
          )}
        </ScrollView>

        {/* Input */}
        <View style={styles.inputContainer}>
          <TextInput
            style={styles.textInput}
            value={newMessage}
            onChangeText={setNewMessage}
            placeholderTextColor="#6D759E"
            placeholder={
              chatType === 'AI'
                ? 'Ask your AI coach...'
                : 'Message your trainer...'
            }
            multiline
            maxLength={500}
          />
          <TouchableOpacity
            style={[styles.sendButton, (!newMessage.trim() || sending) && styles.sendButtonDisabled]}
            onPress={sendMessage}
            disabled={!newMessage.trim() || sending}
          >
            <Ionicons name="send" size={20} color="white" />
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0B0E14',
    paddingBottom: 0,
    marginBottom: 0,
  },
  container: {
    flex: 1,
    backgroundColor: '#0B0E14',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    backgroundColor: '#161C2D',
    paddingHorizontal: Math.max(16, SCREEN_WIDTH * 0.05),
    // paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#2C365E',
    shadowcolor: '#FFFFFF',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    minHeight: 80,
  },
  title: {
    fontSize: Math.max(24, SCREEN_WIDTH * 0.07),
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  subtitle: {
    fontSize: Math.max(14, SCREEN_WIDTH * 0.04),
    color: '#6D759E',
    marginTop: 4,
  },
  messagesContainer: {
    flex: 1,
  },
  messagesContent: {
    padding: 20,
    paddingBottom: 10,
  },
  messageContainer: {
    flexDirection: 'row',
    marginBottom: 16,
    alignItems: 'flex-end',
  },
  userMessageContainer: {
    justifyContent: 'flex-end',
  },
  botMessageContainer: {
    justifyContent: 'flex-start',
  },
  messageBubble: {
    maxWidth: '80%',
    padding: 12,
    borderRadius: 16,
  },
  userMessageBubble: {
    backgroundColor: '#3B82F6',
    borderBottomRightRadius: 4,
  },
  botMessageBubble: {
    backgroundColor: '#161C2D',
    borderBottomLeftRadius: 4,
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  messageText: {
    fontSize: 16,
    lineHeight: 20,
  },
  userMessageText: {
    color: 'white',
  },
  botMessageText: {
    color: '#FFFFFF',
  },
  messageTime: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.7)',
    marginTop: 4,
    textAlign: 'right',
  },
  botMessageTime: {
    color: '#6D759E',
    textAlign: 'left',
  },
  botAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#3B82F6',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#FFFFFF',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 16,
    color: '#6D759E',
    textAlign: 'center',
    lineHeight: 22,
  },
  typingContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginBottom: 16,
  },
  typingBubble: {
    backgroundColor: '#161C2D',
    padding: 12,
    borderRadius: 16,
    borderBottomLeftRadius: 4,
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  typingText: {
    fontSize: 14,
    color: '#6D759E',
    fontStyle: 'italic',
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    padding: 20,
    backgroundColor: '#161C2D',
    borderTopWidth: 1,
    borderTopColor: '#2C365E',
  },
  textInput: {
    flex: 1,
    backgroundColor: '#0B0E14',
    color: '#FFFFFF',
    padding: 12,
    borderRadius: 20,
    fontSize: 16,
    maxHeight: 100,
    marginRight: 12,
  },
  sendButton: {
    backgroundColor: '#3B82F6',
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendButtonDisabled: {
    backgroundColor: '#6D759E',
  },
  unavailableContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  unavailableTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#FFFFFF',
    marginTop: 16,
    marginBottom: 8,
  },
  unavailableText: {
    fontSize: 16,
    color: '#6D759E',
    textAlign: 'center',
    marginBottom: 8,
  },
  unavailableSubtext: {
    fontSize: 14,
    color: '#6D759E',
    textAlign: 'center',
    lineHeight: 20,
  },
});

export default ChatScreen;
